import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage } from '@/hooks/useTeamConversation';

export interface ThreadItemContext {
	id: string;
	title: string | null;
	image_url: string | null;
	asking_price_cents: number | null;
	set_name?: string | null;
	condition?: string | null;
	has_proof_pack?: boolean | null;
}

/**
 * Given a thread's messages, returns the most recent item_id mentioned
 * (via item_card / offer / counter_offer / meetup metadata) and fetches
 * its public detail row. Powers the sticky "Now discussing" pin.
 */
export function useThreadItemContext(messages: ChatMessage[]) {
	const latestItemId = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const m = messages[i] as any;
			const id = m?.metadata?.item_id;
			if (id && typeof id === 'string') return id;
		}
		return null;
	}, [messages]);

	const [items, setItems] = useState<Record<string, ThreadItemContext>>({});

	useEffect(() => {
		if (!latestItemId || items[latestItemId]) return;
		let cancelled = false;
		(async () => {
			// Buyer-facing thread context: read through public_user_items view.
			const { data, error } = await (supabase as any)
				.from('public_user_items')
				.select(
					'id, title, asking_price_cents, set_name, condition, has_proof_pack'
				)
				.eq('id', latestItemId)
				.maybeSingle();
			if (cancelled || error || !data) return;
			const { data: imgs } = await supabase
				.from('user_item_images')
				.select('image_url, display_order')
				.eq('item_id', latestItemId);
			const sorted = (imgs || []).slice().sort(
				(a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)
			);
			setItems((prev) => ({
				...prev,
				[latestItemId]: {
					id: data.id,
					title: (data as any).title ?? null,
					image_url: sorted?.[0]?.image_url ?? null,
					asking_price_cents: (data as any).asking_price_cents ?? null,
					set_name: (data as any).set_name ?? null,
					condition: (data as any).condition ?? null,
					has_proof_pack: (data as any).has_proof_pack ?? null,
				},
			}));
		})();
		return () => {
			cancelled = true;
		};
	}, [latestItemId, items]);

	const current = latestItemId ? items[latestItemId] ?? null : null;
	return { current, items, latestItemId };
}
